// When an action doesn't land (the queue's refusals, an accept the coach
// couldn't apply as it stood), the athlete reads it in the coach's voice:
// never their fault, never the backend's internals, always a way forward.
// The backend's own reason is kept for support behind "Details". Pure, so
// it is tested without a browser; the mobile app has the same rules in
// lib/v2/failures.ts.

/** Deleting something already gone (404) is what the athlete wanted: a success. */
const DELETES = { remove: 'sessions', note_delete: 'notes', race_delete: 'races', rest_remove: 'rest' };
export const alreadyGone = (cmd, status) => status === 404 && Boolean(DELETES[cmd?.kind]);
/** The calendar map an already-gone delete clears its row from. */
export const goneFrom = (cmd) => DELETES[cmd?.kind] || null;

// One at a time per session: a second identical remove or move while the
// first is still on its way is the same tap again, not a new edit.
const ONE_AT_A_TIME = new Set(['remove', 'move', 'outline_remove', 'outline_move', 'done', 'rest_add', 'rest_remove',
  'race_on', 'race_off']);
const MOVES = new Set(['move', 'outline_move']);
const IN_FLIGHT = new Set(['queued', 'sending', 'sent']);

/** The command already on its way that `command` repeats, if any. */
export function duplicateOf(pending, command) {
  const op = command?.op;
  if (!op || !ONE_AT_A_TIME.has(command.kind) || op.id == null) return null;
  return (pending || []).find((c) => c.kind === command.kind && IN_FLIGHT.has(c.status)
    && c.op && String(c.op.id) === String(op.id) && (!MOVES.has(command.kind) || c.op.to === op.to)
    && (command.kind !== 'done' || c.op.done === op.done)) || null;
}

/** The backend's reason as text, for Details (never the headline). */
export function backendReason(data) {
  const d = data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return '';
}

// A 409 that says the thing is already done is the answer the athlete wanted
// (another tap, another device): a success, not a failure. One that says it
// can't be done any more (superseded, undone) won't work on a second try
// either, so it offers no Try again (aithlo-frontend#68).
const SETTLED = {
  accept: { done: /already accepted/i, final: /already (dismissed|undone|superseded)/i },
  dismiss: { done: /already dismissed/i, final: /already (accepted|undone|superseded)/i },
  undo: { done: /\bis undone\b/i, final: /only applied changes undo|is superseded|is no longer open/i },
};

/** 'success', 'final' (refused for good: no Try again) or null, for a command's 409. */
export function settledConflict(cmd, status, data) {
  const rule = SETTLED[cmd?.kind];
  if (status !== 409 || !rule) return null;
  const why = backendReason(data);
  if (rule.done.test(why)) return 'success';
  if (rule.final.test(why)) return 'final';
  return null;
}

// A proposal the calendar changed under (backend app/plan/staleness.py): Accept
// answers 409 code 'proposal_stale' and the card turns into Refresh by itself
// (its updated item arrives on the stream). Not retried: Refresh is the way on.
export const PROPOSAL_STALE_TEXT = 'Your calendar changed since your coach wrote this, so nothing was applied. Tap Refresh on the card.';
export const isProposalStale = (status, data) => status === 409
  && (data?.code === 'proposal_stale' || data?.detail?.code === 'proposal_stale');

/** The version a 409 says is current (top level, or inside `detail`). */
export const currentVersion = (data) => data?.current_version ?? data?.detail?.current_version ?? null;

/**
 * The sessions a 409 names (a rest day asked for on a day that still has
 * planned sessions): not a stale version, so it isn't retried with a new one.
 */
export const collidingSessions = (data) => {
  const ids = data?.session_ids ?? data?.detail?.session_ids;
  return Array.isArray(ids) ? ids : null;
};

/** What a queued command was, without the queue's bookkeeping: enough to send it again. */
export function commandOf(c) {
  const { kind, method, path, body, versioned, label, op, subject, then } = c;
  return { kind, method, path, body, versioned, label, op, subject, ...(then ? { then } : {}) };
}

const dayText = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'that day';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const VERBS = /^(Move|Edit|Skip|Remove|Add|Clear|Log|Mark|Un-tick|Swap)\s+/;
const subjectOf = (f) => f.subject || (f.label || '').replace(VERBS, '') || null;
const UNCHANGED = 'your plan is unchanged.';
const q = (s) => `“${s}”`;

/** The headline for a failure entry: short, warm, and with nothing to blame. */
export function failureText(f) {
  if (f.friendly) return f.friendly;
  const s = subjectOf(f);
  switch (f.kind) {
    case 'move': return `Couldn't move ${q(s)} — ${UNCHANGED}`;
    case 'edit': return f.op?.fields?.structure
      ? `Couldn't save the steps of ${q(s)} as you wrote them — ${UNCHANGED}`
      : `Couldn't save your changes to ${q(s)} — ${UNCHANGED}`;
    case 'release': return `Couldn't hand ${q(s)} back to your coach — ${UNCHANGED}`;
    case 'skip': return `Couldn't skip ${q(s)} — ${UNCHANGED}`;
    case 'remove': return `Couldn't remove ${q(s)} — ${UNCHANGED}`;
    case 'done': return f.op?.done === false
      ? `Couldn't un-tick ${q(s)} just now — ${UNCHANGED}`
      : `Couldn't mark ${q(s)} done just now — ${UNCHANGED}`;
    case 'swap': return f.op?.a && f.op?.b
      ? `Couldn't swap ${q(f.op.a.title)} and ${q(f.op.b.title)} — ${UNCHANGED}`
      : `Couldn't swap those sessions — ${UNCHANGED}`;
    case 'link': return f.op?.session_id == null
      ? "Couldn't mark that activity as extra just now — nothing has changed."
      : `Couldn't link that activity to ${q(s)} just now — nothing has changed.`;
    case 'outline_move': return `Couldn't move ${q(s)} — ${UNCHANGED}`;
    case 'outline_remove': return `Couldn't remove ${q(s)} — ${UNCHANGED}`;
    case 'event_add': return "Couldn't let your coach know you can't train that day just now — your plan is unchanged.";
    case 'create': return `Couldn't add ${q(s)} — ${UNCHANGED}`;
    case 'rest_add': return f.sessions?.length
      ? `${dayText(f.op?.date)} still has a session planned, so it isn't a rest day yet. Move the session first, or make it a rest day again and take it off.`
      : `Couldn't make ${dayText(f.op?.date)} a rest day — ${UNCHANGED}`;
    case 'rest_remove': return `Couldn't make ${dayText(f.op?.date)} a training day again — ${UNCHANGED}`;
    case 'race_on': return `Couldn't make ${q(s)} a race — ${UNCHANGED}`;
    case 'race_off': return `Couldn't turn ${q(s)} back into a session — ${UNCHANGED}`;
    case 'race_add': return `Couldn't add ${q(s)} just now — ${UNCHANGED}`;
    case 'race_edit': return `Couldn't save your changes to ${q(s)} — ${UNCHANGED}`;
    case 'race_priority': return `Couldn't set how much ${q(s || 'that race')} matters just now — ${UNCHANGED}`;
    case 'goal': return `Couldn't save your goal just now — ${UNCHANGED}`;
    case 'onboarding_complete': return "Couldn't start your coach just now: try again.";
    case 'accept': return `Couldn't apply your coach's suggestion — ${UNCHANGED}`;
    case 'dismiss': return "Couldn't set that suggestion aside just now — nothing has changed.";
    case 'refresh': return "Couldn't ask your coach to refresh that suggestion just now — nothing has changed.";
    case 'undo': return `Couldn't undo that change just now — ${UNCHANGED}`;
    case 'rebuild': return `Couldn't reach your coach to rebuild your plan — ${UNCHANGED}`;
    case 'fill': return `Couldn't reach your coach to fill your plan — ${UNCHANGED}`;
    case 'message': return "Couldn't send your message just now.";
    case 'checkin': return "Couldn't save your check-in just now.";
    case 'note_add':
    case 'note_edit': return "Couldn't save your note just now.";
    case 'note_delete': return "Couldn't remove your note just now.";
    case 'intake_answer': return "Couldn't save your answer just now.";
    case 'close_edits': return "Couldn't let your coach know you've finished editing just now.";
    case 'injury_report': return "Couldn't pass that on to your coach just now. If it's serious, don't wait: stop and get it looked at.";
    case 'injury_clear': return `Couldn't mark ${q(s)} as better just now.`;
    default: return `Couldn't save ${q(s || 'that')} — ${UNCHANGED}`;
  }
}

/** The small print under Details: the backend's reason and its status, for support. */
export function failureDetails(f) {
  if (f.friendly) return '';          // already the whole story
  return [f.reason || null, f.status ? `(${f.status})` : null].filter(Boolean).join(' ');
}

/** A failure the athlete can send again (a refused command; not a read-only viewer's). */
export const canRetry = (f) => f.retry !== false && Boolean(f.command);

// ---- a form's own v2 action (actions.js call: onboarding, goal, race) --------------

const FORM_FAILURE = {
  onboarding: "Couldn't start your coach just now: try again.",
  onboarding_complete: "Couldn't start your coach just now: try again.",
  goal: "Couldn't save your goal just now: try again.",
  race: "Couldn't save your race just now: try again.",
  race_add: "Couldn't add your race just now: try again.",
  race_edit: "Couldn't save your changes to that race just now: try again.",
  race_priority: "Couldn't save how much that race matters just now: try again.",
  injury_report: "Couldn't pass that on to your coach just now. If it's serious, don't wait: stop and get it looked at.",
  injury_clear: "Couldn't mark that as better just now: try again.",
};
export const OFFLINE_TEXT = "You're offline: check your connection and try again.";

/**
 * A form's refused or offline action (CommandRefused, or {network}) as the
 * form shows it: a warm headline, and the backend's reason only as details.
 */
export function formFailure(kind, e) {
  if (e?.network) return { text: OFFLINE_TEXT, details: '' };
  return {
    text: FORM_FAILURE[kind] || "That didn't save just now: try again.",
    details: [e?.detail || null, e?.status ? `(${e.status})` : null].filter(Boolean).join(' '),
  };
}

// ---- an accept the coach answered in words (HTTP 202) -------------------------------

export const ACCEPT_STATUS = ['updating', 'kept_current'];
const STATUS_FALLBACK = {
  updating: 'Your coach is updating this plan…',
  kept_current: "Your coach couldn't apply this one, so your current plan is unchanged.",
};

/** An accept's answer as the card shows it, or null for a plain success. */
export function acceptOutcome(data) {
  if (!data || !ACCEPT_STATUS.includes(data.status)) return null;
  return { status: data.status, message: (typeof data.message === 'string' && data.message.trim()) || STATUS_FALLBACK[data.status] };
}

/** What the client remembers about an item after its command answered: an accept's outcome, or a rebuild asked for. */
export function outcomeFor(cmd, data) {
  if (cmd?.op?.id == null) return null;
  if (cmd.kind === 'accept') return acceptOutcome(data);
  if (cmd.kind === 'rebuild' || cmd.kind === 'fill') return { status: 'rebuilding' };
  return null;
}

/** The outcome a still-active item shows; once the item moves on (superseded, accepted) it speaks for itself. */
export function itemOutcome(item, outcomes) {
  if (!item || item.state !== 'active') return null;
  return outcomes?.[String(item.id)] || null;
}

/** Which answers a card still offers once its accept answered in words (null: all of them). */
export const answersAfter = (o) => (o?.status === 'updating' ? [] : o?.status === 'kept_current' ? ['dismiss'] : null);

// ---- the coach's failure messages (coach_message, body.kind plan_failed …) ----------

// plan_paused: the spend guard held a build back (backend coach_port._paused); it ends the build too
export const REBUILD_KINDS = ['plan_failed', 'plan_not_applied', 'proposal_updating', 'plan_paused'];
export const REBUILDING_TEXT = 'Your coach is on it…';

/** A coach message that offers "Try again" (body.actions includes 'rebuild', or 'fill'). */
export const offersRebuild = (item) => item?.type === 'coach_message' && Array.isArray(item.body?.actions)
  && (item.body.actions.includes('rebuild') || item.body.actions.includes('fill'));

/** An empty calendar's offer to fill it from the plan (body.actions 'fill'): POST /v2/plan/fill, not a new plan. */
export const offersFill = (item) => item?.type === 'coach_message' && Array.isArray(item.body?.actions)
  && item.body.actions.includes('fill');

/** The button's words: a fill's own (body.fill_label, "Fill my next two weeks"); else the message's
 * (an empty calendar's "Build my plan"), else Try again. */
export const rebuildLabel = (item) => {
  const own = (k) => (typeof item?.body?.[k] === 'string' && item.body[k].trim()) || null;
  if (offersFill(item)) return own('fill_label') || 'Fill my next two weeks';
  return own('action_label') || 'Try again';
};

/** The command the button sends: the fill when the message offers one, else the rebuild. */
export const rebuildCommand = (item, commands) => (offersFill(item) ? commands.fillPlan(item) : commands.rebuildPlan(item));

/** The rebuild (or fill) for this message is asked for: on its way, or answered. */
export const rebuildAsked = (item, pending = [], outcomes = {}) => outcomes?.[String(item.id)]?.status === 'rebuilding'
  || pending.some((c) => (c.op?.kind === 'rebuild' || c.op?.kind === 'fill') && String(c.op.id) === String(item.id));
